import { skipToken, useInfiniteQuery } from '@tanstack/react-query';
import { useMemo } from 'react';

import { Clips } from '@air/api';
import { type ClipType, SortDirection } from '@air/api/types';
import { callAirApi } from '@air/utils-api';

import { toWorkspaceAgentAssetMentionItem } from '~/components/v2/components/workspace/workspace-agent-panel/mentions/toWorkspaceAgentAssetMentionItem';
import {
  type WorkspaceAgentMentionSearchParams,
  type WorkspaceAgentMentionSearchResult,
} from '~/components/v2/components/workspace/workspace-agent-panel/mentions/types';
import {
  useWorkspaceAgentMentionSearchQueries,
  useWorkspaceAgentMentionSearchResult,
} from '~/components/v2/components/workspace/workspace-agent-panel/mentions/useWorkspaceAgentMentionSearchResult';
import { WORKSPACE_AGENT_MENTION_PAGE_SIZE } from '~/components/v2/components/workspace/workspace-agent-panel/mentions/workspaceAgentMentionConstants';
import {
  getPrivateWorkspaceAgentBoardAssetSearchFilters,
  type PrivateWorkspaceAgentBoardAssetSearchFilters,
} from '~/components/v2/components/workspace/workspace-agent-panel/queries/getPrivateWorkspaceAgentBoardAssetSearchFilters';
import { PRIVATE_WORKSPACE_AGENT_ASSET_MENTION_CLIP_TYPES } from '~/components/v2/components/workspace/workspace-agent-panel/queries/usePrivateWorkspaceAgentAssetMentionSearch';

export const PRIVATE_WORKSPACE_AGENT_BOARD_ASSET_SEARCH_KEY = 'PRIVATE_WORKSPACE_AGENT_BOARD_ASSET_SEARCH';

export type PrivateWorkspaceAgentBoardAssetSearchParams = {
  boardId: string | undefined;
  filters?: PrivateWorkspaceAgentBoardAssetSearchFilters;
  search: string;
  types: readonly ClipType[];
  workspaceId: string | undefined;
};

export type PrivateWorkspaceAgentBoardAssetSearchKey = [
  typeof PRIVATE_WORKSPACE_AGENT_BOARD_ASSET_SEARCH_KEY,
  PrivateWorkspaceAgentBoardAssetSearchParams,
];

export const getPrivateWorkspaceAgentBoardAssetSearchKey = (
  params: PrivateWorkspaceAgentBoardAssetSearchParams,
): PrivateWorkspaceAgentBoardAssetSearchKey => [PRIVATE_WORKSPACE_AGENT_BOARD_ASSET_SEARCH_KEY, params];

export type UsePrivateWorkspaceAgentBoardAssetSearchParams = WorkspaceAgentMentionSearchParams & {
  boardId: string | undefined;
  filters?: PrivateWorkspaceAgentBoardAssetSearchFilters;
  types?: readonly ClipType[];
};

/**
 * Lexical `Clips.search` for the asset picker. With a board id it is board-scoped; without one it
 * lists the workspace. Keyed apart from the mention stream and from `getClipsKey`, so a gallery on
 * the same board cannot share pages with this picker.
 */
export const usePrivateWorkspaceAgentBoardAssetSearch = ({
  boardId,
  enabled,
  filters,
  query,
  types = PRIVATE_WORKSPACE_AGENT_ASSET_MENTION_CLIP_TYPES,
  workspaceId,
}: UsePrivateWorkspaceAgentBoardAssetSearchParams): WorkspaceAgentMentionSearchResult => {
  const queries = useWorkspaceAgentMentionSearchQueries({ enabled, query });
  const { debouncedQuery } = queries;
  const clipTypes = types.length > 0 ? types : PRIVATE_WORKSPACE_AGENT_ASSET_MENTION_CLIP_TYPES;
  const searchFilters = getPrivateWorkspaceAgentBoardAssetSearchFilters({ boardId, filters });

  const search = useInfiniteQuery({
    queryKey: getPrivateWorkspaceAgentBoardAssetSearchKey({
      boardId,
      filters,
      search: debouncedQuery,
      types: clipTypes,
      workspaceId,
    }),
    queryFn: !workspaceId
      ? skipToken
      : ({ pageParam }: { pageParam: string }) =>
          callAirApi(() =>
            Clips.search({
              ...(boardId ? { boardId } : {}),
              cursor: pageParam || undefined,
              filters: searchFilters,
              limit: WORKSPACE_AGENT_MENTION_PAGE_SIZE,
              search: debouncedQuery || undefined,
              semantic: false,
              sortField: { direction: SortDirection.desc, name: 'dateModified' },
              types: [...clipTypes],
              workspaceId,
            }),
          ),
    enabled: !!workspaceId && enabled,
    getNextPageParam: (lastPage) => lastPage?.pagination?.cursor ?? undefined,
    initialPageParam: '',
  });

  const { data } = search;

  const items = useMemo(
    () => data?.pages.flatMap((page) => page.data.clips.map(toWorkspaceAgentAssetMentionItem)),
    [data],
  );

  return useWorkspaceAgentMentionSearchResult({ items, queries, search });
};
